import { I18n } from 'react-redux-i18n';
import { Badge, Tooltip, Typography } from 'antd';

import {
  FiltersTypes,
  WineColorType,
  wineColorsRGB
} from 'interface/Distribution/globalView';

const isMobile = window.innerWidth < 1200;

const { Text } = Typography;

const renderRefsCount = (value: string) => {
  if (!value || value === '') return 0;
  const items = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return items.length;
};

const renderRefsList = (value: string) => {
  if (!value || value === '') return '-';
  const items = value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (items.length === 0) return '-';
  return items.join(', ');
};

export const getWinesAndBrandsColumns = (
  filters: FiltersTypes,
  currency: string
) =>
  [
    {
      name: I18n.t('globalView.kpi.columnsTable.rank'),
      title: I18n.t('globalView.kpi.columnsTable.rank'),
      dataIndex: 'rank',
      defaultSortOrder: 'ascend',
      sorter: (a: any, b: any) => a.rank - b.rank,
      align: 'center',
      width: 110,
      render: (value: number) => (
        <div style={{ textAlign: 'center' }}>{value}</div>
      )
    },
    {
      name: (() => {
        if (filters.tableTypeActive === 'wine') {
          if (filters.wineType === '2') {
            return I18n.t('globalView.filters.typesTable.spirit');
          }
          return I18n.t('globalView.filters.typesTable.wine');
        }
        if (['brand', 'spirit'].includes(filters.tableTypeActive)) {
          return I18n.t(
            `globalView.filters.typesTable.${filters.tableTypeActive}`
          );
        }
        return I18n.t(`globalView.filters.entityNames.${filters.wineType}`);
      })(),
      title: () => {
        if (filters.tableTypeActive === 'wine') {
          // When viewing wine table, check if spirit category is selected
          if (filters.wineType === '2') {
            return I18n.t('globalView.filters.typesTable.spirit');
          }
          return I18n.t('globalView.filters.typesTable.wine');
        }
        if (['brand', 'spirit'].includes(filters.tableTypeActive)) {
          return I18n.t(
            `globalView.filters.typesTable.${filters.tableTypeActive}`
          );
        }
        return I18n.t(`globalView.filters.entityNames.${filters.wineType}`);
      },
      dataIndex: 'entity_name',
      sorter: (a: any, b: any) => a.entity_name.localeCompare(b.entity_name),
      render: (value: string) => <Text strong>{value}</Text>
    },
    filters.tableTypeActive === 'wine' && filters.wineType === '1'
      ? {
          name: I18n.t('globalView.kpi.columnsTable.color'),
          title: () => (
            <Tooltip title={I18n.t('globalView.kpi.columnDescriptions.color')}>
              {I18n.t('globalView.kpi.columnsTable.color')}
            </Tooltip>
          ),
          dataIndex: 'color',
          sorter: (a: any, b: any) => a.color.localeCompare(b.color),
          render: (text: WineColorType) => (
            <Badge
              color={wineColorsRGB[text]}
              text={I18n.t(`globalView.filters.options.colorsWine.${text}`)}
            />
          )
        }
      : filters.tableTypeActive === 'wine' &&
        filters.wineType === '2' && {
          name: I18n.t('globalView.kpi.columnsTable.spiritType'),
          title: () => (
            <Tooltip
              title={I18n.t('globalView.kpi.columnDescriptions.spiritType')}
            >
              {I18n.t('globalView.kpi.columnsTable.spiritType')}
            </Tooltip>
          ),
          dataIndex: 'wine_sub_type_name',
          sorter: (a: any, b: any) =>
            (a.wine_sub_type_name || '').localeCompare(
              b.wine_sub_type_name || ''
            )
        },
    {
      name: I18n.t('globalView.kpi.columnsTable.facing'),
      title: () => (
        <Tooltip title={I18n.t('globalView.kpi.columnDescriptions.facing')}>
          {I18n.t('globalView.kpi.columnsTable.facing')}
        </Tooltip>
      ),
      dataIndex: 'facing',
      sorter: (a: any, b: any) => (a.facing || 0) - (b.facing || 0)
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.dn'),
      title: () => (
        <Tooltip title={I18n.t('globalView.kpi.columnDescriptions.dn')}>
          {I18n.t('globalView.kpi.columnsTable.dn')}
        </Tooltip>
      ),
      dataIndex: 'dn',
      sorter: (a: any, b: any) => (a.dn || 0) - (b.dn || 0)
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.avPrice'),
      title: () => I18n.t('globalView.kpi.columnsTable.avPrice'),
      dataIndex: 'av_price',
      sorter: (a: any, b: any) => (a.av_price || 0) - (b.av_price || 0),
      render: (value?: number) =>
        value ? (
          <span>
            {value.toLocaleString('fr-FR', {
              style: 'currency',
              currency: currency,
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}
          </span>
        ) : (
          '-'
        )
    }
  ].filter((item) => item);

export const getPosColumns = (
  hasClientCustomRefs: boolean = false,
  hasRivalCustomRefs: boolean = false,
  onStoreClick?: (storeId: string) => void
) =>
  [
    {
      name: I18n.t('globalView.kpi.columnsTable.rank'),
      title: I18n.t('globalView.kpi.columnsTable.rank'),
      dataIndex: 'rank',
      defaultSortOrder: 'ascend',
      sorter: (a: any, b: any) => a.rank - b.rank,
      align: 'center',
      width: 110,
      render: (value: number) => (
        <div style={{ textAlign: 'center' }}>{value}</div>
      )
    },
    {
      name: I18n.t('globalView.filters.entityNames.point_of_sale'),
      title: () => {
        return I18n.t(`globalView.filters.entityNames.point_of_sale`);
      },
      dataIndex: 'entity_name',
      width: 200,
      sorter: (a: any, b: any) => a.entity_name.localeCompare(b.entity_name),
      render: (value: string, record: any) => {
        const storeId = record.store_id || record.entity_id;
        if (onStoreClick && storeId) {
          return (
            <Text
              strong
              className="pos-cell-clickable"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onStoreClick(storeId);
              }}
            >
              {value}
            </Text>
          );
        }
        return <Text strong>{value}</Text>;
      }
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.address'),
      title: () => I18n.t('globalView.kpi.columnsTable.address'),
      width: 200,
      dataIndex: 'store_address',
      hiddenByDefault: true,
      sorter: (a: any, b: any) =>
        (a.store_address || '').localeCompare(b.store_address || '')
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.postcode'),
      title: () => I18n.t('globalView.kpi.columnsTable.postcode'),
      width: 100,
      dataIndex: 'store_postcode',
      hiddenByDefault: true,
      sorter: (a: any, b: any) =>
        (a.store_postcode || '').localeCompare(b.store_postcode || '')
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.city_name'),
      title: () => (
        <Tooltip title={I18n.t('globalView.kpi.columnDescriptions.city_name')}>
          {I18n.t('globalView.kpi.columnsTable.city_name')}
        </Tooltip>
      ),
      width: 150,
      dataIndex: 'store_city_name',
      sorter: (a: any, b: any) =>
        (a.store_city_name || '').localeCompare(b.store_city_name || '')
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.market'),
      title: () => I18n.t('globalView.kpi.columnsTable.market'),
      width: 100,
      dataIndex: 'market_name',
      hiddenByDefault: true,
      sorter: (a: any, b: any) =>
        (a.market_name || '').localeCompare(b.market_name || '')
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.facing'),
      width: 120,
      title: () => I18n.t('globalView.kpi.columnsTable.facing'),
      dataIndex: 'facing',
      sorter: (a: any, b: any) => (a.facing || 0) - (b.facing || 0)
    },
    !hasClientCustomRefs && {
      name: I18n.t('globalView.kpi.columnsTable.client_refs'),
      title: () => I18n.t('globalView.kpi.columnsTable.client_refs'),
      width: 120,
      dataIndex: 'client_refs',
      render: renderRefsCount,
      sorter: (a: any, b: any) => {
        const aRefsLength =
          a.client_refs == '' ? 0 : a.client_refs.split(',').length;
        const bRefsLength =
          b.client_refs == '' ? 0 : b.client_refs.split(',').length;

        return aRefsLength - bRefsLength;
      }
    },
    hasClientCustomRefs && {
      name: I18n.t('globalView.kpi.columnsTable.client_custom_refs'),
      title: () => I18n.t('globalView.kpi.columnsTable.client_custom_refs'),
      width: 120,
      dataIndex: 'client_custom_refs',
      render: renderRefsCount,
      sorter: (a: any, b: any) => {
        const aRefsLength =
          a.client_custom_refs == ''
            ? 0
            : a.client_custom_refs.split(',').length;
        const bRefsLength =
          b.client_custom_refs == ''
            ? 0
            : b.client_custom_refs.split(',').length;

        return aRefsLength - bRefsLength;
      }
    },
    !hasClientCustomRefs && {
      name: I18n.t('globalView.kpi.columnsTable.client_refs_list'),
      title: () => I18n.t('globalView.kpi.columnsTable.client_refs_list'),
      width: 200,
      dataIndex: 'client_refs',
      key: 'client_refs_list',
      render: renderRefsList,
      sorter: (a: any, b: any) =>
        (a.client_refs || '').localeCompare(b.client_refs || '')
    },
    hasClientCustomRefs && {
      name: I18n.t('globalView.kpi.columnsTable.client_custom_refs_list'),
      title: () =>
        I18n.t('globalView.kpi.columnsTable.client_custom_refs_list'),
      width: 200,
      dataIndex: 'client_custom_refs',
      key: 'client_custom_refs_list',
      render: renderRefsList,
      sorter: (a: any, b: any) =>
        (a.client_custom_refs || '').localeCompare(b.client_custom_refs || '')
    },
    !hasRivalCustomRefs && {
      name: I18n.t('globalView.kpi.columnsTable.rival_refs'),
      title: () => I18n.t('globalView.kpi.columnsTable.rival_refs'),
      width: 120,
      dataIndex: 'rival_refs',
      render: renderRefsCount,
      sorter: (a: any, b: any) => {
        const aRefsLength =
          a.rival_refs == '' ? 0 : a.rival_refs.split(',').length;
        const bRefsLength =
          b.rival_refs == '' ? 0 : b.rival_refs.split(',').length;

        return aRefsLength - bRefsLength;
      }
    },
    hasRivalCustomRefs && {
      name: I18n.t('globalView.kpi.columnsTable.rival_custom_refs'),
      title: () => I18n.t('globalView.kpi.columnsTable.rival_custom_refs'),
      width: 120,
      dataIndex: 'rival_custom_refs',
      render: renderRefsCount,
      sorter: (a: any, b: any) => {
        const aRefsLength =
          a.rival_custom_refs == '' ? 0 : a.rival_custom_refs.split(',').length;
        const bRefsLength =
          b.rival_custom_refs == '' ? 0 : b.rival_custom_refs.split(',').length;

        return aRefsLength - bRefsLength;
      }
    },
    !hasRivalCustomRefs && {
      name: I18n.t('globalView.kpi.columnsTable.rival_refs_list'),
      title: () => I18n.t('globalView.kpi.columnsTable.rival_refs_list'),
      width: 200,
      dataIndex: 'rival_refs',
      key: 'rival_refs_list',
      render: renderRefsList,
      sorter: (a: any, b: any) =>
        (a.rival_refs || '').localeCompare(b.rival_refs || '')
    },
    hasRivalCustomRefs && {
      name: I18n.t('globalView.kpi.columnsTable.rival_custom_refs_list'),
      title: () => I18n.t('globalView.kpi.columnsTable.rival_custom_refs_list'),
      width: 200,
      dataIndex: 'rival_custom_refs',
      key: 'rival_custom_refs_list',
      render: renderRefsList,
      sorter: (a: any, b: any) =>
        (a.rival_custom_refs || '').localeCompare(b.rival_custom_refs || '')
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.avPrice'),
      title: () => (
        <Tooltip title={I18n.t('globalView.kpi.columnDescriptions.avPrice')}>
          {I18n.t('globalView.kpi.columnsTable.avPrice')}
        </Tooltip>
      ),
      dataIndex: 'av_price',
      width: 120,
      sorter: (a: any, b: any) => (a.av_price || 0) - (b.av_price || 0),
      render: (value?: number, row?: any) =>
        value ? (
          <span>
            {value.toLocaleString('fr-FR', {
              style: 'currency',
              currency: row?.displayed_currency || 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}
          </span>
        ) : (
          '-'
        )
    }
  ].filter((item) => item);

export const getPositioningColumns = () =>
  [
    {
      name: I18n.t('globalView.kpi.columnsTable.rank'),
      title: I18n.t('globalView.kpi.columnsTable.rank'),
      dataIndex: 'rank',
      defaultSortOrder: 'ascend',
      sorter: (a: any, b: any) => a.rank - b.rank,
      align: 'center',
      width: 110,
      render: (value: number) => (
        <div style={{ textAlign: 'center' }}>{value}</div>
      )
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.market'),
      title: () => {
        return I18n.t(`globalView.kpi.columnsTable.market`);
      },
      dataIndex: 'entity_name',
      width: isMobile ? 150 : 250,
      sorter: (a: any, b: any) => a.entity_name.localeCompare(b.entity_name),
      render: (value: string) => <Text strong>{value}</Text>
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.dn_client'),
      title: () => (
        <Tooltip title={I18n.t('globalView.kpi.columnDescriptions.dn_client')}>
          {I18n.t('globalView.kpi.columnsTable.dn_client')}
        </Tooltip>
      ),
      dataIndex: 'dn_client',
      width: 100,
      sorter: (a: any, b: any) => (a.dn_client || 0) - (b.dn_client || 0)
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.tp_client'),
      title: () => (
        <Tooltip title={I18n.t('globalView.kpi.columnDescriptions.tp_client')}>
          {I18n.t('globalView.kpi.columnsTable.tp_client')}
        </Tooltip>
      ),
      dataIndex: 'tp_client',
      width: 100,
      sorter: (a: any, b: any) => (a.tp_client || 0) - (b.tp_client || 0),
      render: (value?: number) => (value ? <span>{value} %</span> : '-')
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.dn_rival'),
      title: () => (
        <Tooltip title={I18n.t('globalView.kpi.columnDescriptions.dn_rival')}>
          {I18n.t('globalView.kpi.columnsTable.dn_rival')}
        </Tooltip>
      ),
      dataIndex: 'dn_rival',
      width: 100,
      sorter: (a: any, b: any) => (a.dn_rival || 0) - (b.dn_rival || 0)
    },
    {
      name: I18n.t('globalView.kpi.columnsTable.tp_rival'),
      title: () => (
        <Tooltip title={I18n.t('globalView.kpi.columnDescriptions.tp_rival')}>
          {I18n.t('globalView.kpi.columnsTable.tp_rival')}
        </Tooltip>
      ),
      dataIndex: 'tp_rival',
      width: 100,
      sorter: (a: any, b: any) => (a.tp_rival || 0) - (b.tp_rival || 0),
      render: (value?: number) => (value ? <span>{value} %</span> : '-')
    },
    // {
    //   title: I18n.t('globalView.kpi.columnsTable.z_score'),
    //   dataIndex: 'z_score',
    //   width: 100
    // },
    {
      name: I18n.t('globalView.kpi.columnsTable.dn_total'),
      title: () => (
        <Tooltip title={I18n.t('globalView.kpi.columnDescriptions.dn_total')}>
          {I18n.t('globalView.kpi.columnsTable.dn_total')}
        </Tooltip>
      ),
      dataIndex: 'dn_total',
      width: 100,
      sorter: (a: any, b: any) => (a.dn_total || 0) - (b.dn_total || 0)
    }
  ].filter((item) => item);
